import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ChangelogService } from '../changelog/changelog.service';
import { UserClaims } from 'src/app/models/user.claims';
import { BehaviorSubject } from 'rxjs';
import { AlertService, MessageSeverity } from '../utilities/alert.service';
import { fetchAuthSession, signInWithRedirect } from '@aws-amplify/auth'
import { signOut } from 'aws-amplify/auth/cognito';
import { LocalStorageKeys } from '../local-storage-keys';


@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  public isExternalPortal: boolean = false;
  private _authSession: any;

  public get IsSignedOut(): boolean {
    let isSignedOutBool = JSON.parse(localStorage.getItem(LocalStorageKeys.USER_IS_SIGNED_OUT));
    return isSignedOutBool;
  }
  public set IsSignedOut(value: boolean) {
    localStorage.setItem(LocalStorageKeys.USER_IS_SIGNED_OUT, value.toString());
  }

  get claims(): UserClaims {
    return this.user.getValue();
  }

  set claims(value: UserClaims) {
    if (environment.simulateRoles != null) {
      value.groups = environment.simulateRoles;
    }
    this.user.next(value);
    this.currentUser = value;
  }
  public user: BehaviorSubject<UserClaims> = new BehaviorSubject<UserClaims>(this.currentUser);

  private sessionLoaded = false;
  private sessionLoadedPromise: Promise<void>;

  // ID TOKEN
  private _idToken: string;
  get idToken(): string {
    return this._idToken;
  }
  set idToken(value: string) {
    this._idToken = value
  }

  // CURRENT USER
  private _currentUser: UserClaims;
  get currentUser(): UserClaims {
    return this._currentUser;
  }
  private set currentUser(value: UserClaims) {
    this._currentUser = value;
  }

  // IS
  get isAuthenticated() {
    return this._authSession || this._idToken || false;
  }

  constructor(
    private router: Router,
    private changelogService: ChangelogService,
    private alertService: AlertService
  ) {
    this.sessionLoadedPromise = this.loadSession();
  }

  private async loadSession(): Promise<void> {
    try {
      this._authSession = await this.handleFetchAuthSession()
      this.sessionLoaded = true;
    } catch (error) {
      console.error('Error loading session:', error);
      this.sessionLoaded = true; 
    }
  }

  // Method to wait session is ready
  async ensureSessionLoaded(): Promise<void> {
    if (!this.sessionLoaded) {
      await this.sessionLoadedPromise;
    }
  }

  // Handle session and refresh
  async handleFetchAuthSession() {
    try {
      this._authSession = await fetchAuthSession({ forceRefresh: true });
      if (!this._authSession.tokens || this.IsSignedOut) {
        this._authSession = null;
        this.router.navigate(['home']);
        this.alertService.showStickyMessage('Sessione chiusa', 'Ripetere il login per accedere al portale', MessageSeverity.info);
        return false;
      }
      this._idToken = this._authSession.tokens.idToken.toString();
      let payLoad = this._authSession.tokens.idToken.payload;
      this.IsSignedOut = false;
      this.claims = {
        company: payLoad.identities[0].providerName,
        email: payLoad.email?.toString(),
        username: payLoad['cognito:username'].toString(),
        family_name: payLoad.family_name.toString(),
        groups: environment.simulateRoles != null ? environment.simulateRoles : payLoad['cognito:groups'],
        name: payLoad.name.toString()
      };

      return true;
    }
    catch (err) {
      this._authSession = null;
      this.router.navigate(['home']);
      this.alertService.showStickyMessage('Sessione chiusa', 'Ripetere il login per accedere al portale', MessageSeverity.info);
      return false;
    }
  }

  // Login current user
  public async login() {
    this.handleSocialSignIn().then(res => {
      this.IsSignedOut = false;
      // Redirect to root
      if (!this.isExternalPortal) {
        this.router.navigate(['pages']);
      }
    }).catch(async error => {
      if (error = 'UserAlreadyAuthenticatedException') {
        await this.handleFetchAuthSession();
      }
      else {
        console.error('Error during handleSocialSignIn', error);
      }
    });
  }

  // Refresh current sessione
  public refreshSession() {
    this.handleFetchAuthSession();
  }

  // Handle sign in with Google
  private handleSocialSignIn() {
    return signInWithRedirect({ provider: { custom: 'Google' } });
  }

  // Handle sign out
  public handleSignOut() {
    sessionStorage.clear();
    // Save the latest changes readed in order not to lose user settings.
    this.changelogService.saveLastChanges();
    signOut().then(res => {
      this.IsSignedOut = true;
      this._idToken = null;
      this._authSession = null;
      this.router.navigate(['home']);
    });
  }
}